import * as Sentry from "@sentry/vue";
import { devLog, devError } from "@/utils/devLog/devLog";
import {
  TWILIO_SESSION_CONTEXT,
  TWILIO_ROOM_CONNECTING,
  TWILIO_ROOM_CONNECTED,
  TWILIO_ROOM_DISCONNECTED,
  TWILIO_ROOM_RECONNECTING,
  TWILIO_ROOM_RECONNECTED,
  TWILIO_PARTICIPANT_CONNECTED,
  TWILIO_PARTICIPANT_DISCONNECTED,
  TWILIO_LOCAL_PERMISSIONS_REQUESTED,
  TWILIO_LOCAL_PERMISSIONS_GRANTED,
  TWILIO_LOCAL_DEVICE_REVOKED,
  TWILIO_LOCAL_PERMISSIONS_DENIED,
  TWILIO_TRACK_PUBLISHED,
  TWILIO_TRACK_UNPUBLISHED,
  TWILIO_TRACK_SUBSCRIBED,
  TWILIO_TRACK_UNSUBSCRIBED,
  TWILIO_TRACK_ENABLED,
  TWILIO_TRACK_DISABLED,
  TWILIO_TRACK_SWITCHED_OFF,
  TWILIO_TRACK_SWITCHED_ON,
  TWILIO_LOCAL_MIC_TOGGLED,
  TWILIO_LOCAL_CAMERA_TOGGLED,
  TWILIO_NETWORK_QUALITY_CHANGED,
  TWILIO_ROOM_CONNECT_FAILED,
  TWILIO_TRACK_PUBLICATION_FAILED,
  TWILIO_PARTICIPANT_TRACKS_AT_HANDLE,
  TWILIO_TRACK_PRESUBSCRIBED_ATTACHED,
  TWILIO_REMOTE_TRACK_STARTED,
} from "@/utils/twilioEvents";

export type TwilioEventData = Record<string, unknown>;

export type TwilioSessionContext = {
  platform: "web";
  session_role: string;
  sdkVersion: string;
  userAgent: string;
  isSupported: boolean;
  devices: MediaDeviceInfo[];
  trackLabels: string[];
};

// Events routed to Sentry.captureMessage at level "info"
const INFO_EVENTS = new Set([
  TWILIO_SESSION_CONTEXT,
  TWILIO_ROOM_CONNECTING,
  TWILIO_ROOM_CONNECTED,
  TWILIO_ROOM_DISCONNECTED,
  TWILIO_ROOM_RECONNECTING,
  TWILIO_ROOM_RECONNECTED,
  TWILIO_PARTICIPANT_CONNECTED,
  TWILIO_PARTICIPANT_DISCONNECTED,
  TWILIO_LOCAL_PERMISSIONS_REQUESTED,
  TWILIO_LOCAL_PERMISSIONS_GRANTED,
  TWILIO_LOCAL_DEVICE_REVOKED,
]);

// Events routed to Sentry.captureMessage at level "error"
const ERROR_EVENTS = new Set([
  TWILIO_LOCAL_PERMISSIONS_DENIED,
  TWILIO_ROOM_CONNECT_FAILED,
  TWILIO_TRACK_PUBLICATION_FAILED,
]);

// Events routed to Sentry.addBreadcrumb
const BREADCRUMB_EVENTS = new Set([
  TWILIO_TRACK_PUBLISHED,
  TWILIO_TRACK_UNPUBLISHED,
  TWILIO_TRACK_SUBSCRIBED,
  TWILIO_TRACK_UNSUBSCRIBED,
  TWILIO_TRACK_ENABLED,
  TWILIO_TRACK_DISABLED,
  TWILIO_TRACK_SWITCHED_OFF,
  TWILIO_TRACK_SWITCHED_ON,
  TWILIO_LOCAL_MIC_TOGGLED,
  TWILIO_LOCAL_CAMERA_TOGGLED,
  TWILIO_NETWORK_QUALITY_CHANGED,
  TWILIO_PARTICIPANT_TRACKS_AT_HANDLE,
  TWILIO_TRACK_PRESUBSCRIBED_ATTACHED,
  TWILIO_REMOTE_TRACK_STARTED,
]);

let loggerInitialised = false;

/**
 * Initialise the Twilio SDK's internal logger and forward every line it emits
 * as a Sentry breadcrumb. Safe to call multiple times — guarded by a module-level flag.
 *
 * Raw Sentry.addBreadcrumb call (not via logTwilioEvent) per spec:
 * category "twilio.sdk", level matching the SDK log level.
 */
export const initialiseTwilioLogger = (): void => {
  if (loggerInitialised) return;
  loggerInitialised = true;

  // twilio-video exposes a loglevel-based Logger on the Video namespace.
  // We grab it via require so this utility stays importable in non-browser test contexts.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const Video = require("twilio-video");
  const sdkLogger = Video.Logger.getLogger("twilio-video");
  sdkLogger.setLevel("debug");

  const sdkLogLevels = ["trace", "debug", "info", "warn", "error"] as const;
  type SdkLogLevel = typeof sdkLogLevels[number];

  sdkLogLevels.forEach((level: SdkLogLevel) => {
    const originalMethod = sdkLogger[level].bind(sdkLogger);
    sdkLogger[level] = (...args: unknown[]): void => {
      originalMethod(...args);
      const message = args.map((argument) => String(argument)).join(" ");
      const sentryLevel = level === "warn" ? "warning" : level;
      Sentry.addBreadcrumb({
        category: "twilio.sdk",
        message,
        level: sentryLevel as Sentry.SeverityLevel,
      });
    };
  });
};

/**
 * Call once at the start of a Twilio session. Sets Sentry scope tags so every
 * subsequent event is filterable by feature:twilio-video and room_sid, then
 * fires TWILIO_SESSION_CONTEXT as an info message with full session context.
 */
export const startTwilioSession = (context: TwilioSessionContext): void => {
  Sentry.configureScope((scope) => {
    scope.setTag("feature", "twilio-video");
    scope.setTag("platform", context.platform);
    scope.setTag("session_role", context.session_role);
    scope.setContext("twilio_session", {
      sdkVersion: context.sdkVersion,
      userAgent: context.userAgent,
      isSupported: context.isSupported,
      deviceCount: context.devices.length,
      trackLabels: context.trackLabels,
    });
  });

  logTwilioEvent(TWILIO_SESSION_CONTEXT, {
    platform: context.platform,
    session_role: context.session_role,
    sdkVersion: context.sdkVersion,
    isSupported: context.isSupported,
    trackLabels: context.trackLabels,
  });
};

/**
 * Call once the room SID is known (immediately after a successful connect()).
 * Adds room_sid to scope tags so every subsequent Sentry event for this session
 * is filterable by room_sid.
 */
export const setTwilioRoomSid = (roomSid: string): void => {
  Sentry.configureScope((scope) => {
    scope.setTag("room_sid", roomSid);
  });
};

/**
 * Log a Twilio lifecycle event. Internally routes to the appropriate Sentry call
 * based on the event name:
 *   - INFO_EVENTS    → Sentry.captureMessage(name, "info")
 *   - ERROR_EVENTS   → Sentry.captureMessage(name, "error")
 *   - BREADCRUMB_EVENTS → Sentry.addBreadcrumb(...)
 *
 * Call sites remain Sentry-agnostic.
 */
export const logTwilioEvent = (
  eventName: string,
  data?: TwilioEventData,
): void => {
  // Mirror to the console so these events appear alongside the Twilio SDK's
  // own logger output during live debugging — Sentry breadcrumbs alone are not
  // visible when reading the console. Gated to dev/staging so production
  // consoles stay clean.
  devLog(`[twilio-event] ${eventName}`, data ?? "");

  if (INFO_EVENTS.has(eventName)) {
    Sentry.withScope((scope) => {
      if (data) scope.setExtras(data);
      Sentry.captureMessage(eventName, "info");
    });
    return;
  }

  if (ERROR_EVENTS.has(eventName)) {
    Sentry.withScope((scope) => {
      if (data) scope.setExtras(data);
      Sentry.captureMessage(eventName, "error");
    });
    return;
  }

  if (BREADCRUMB_EVENTS.has(eventName)) {
    Sentry.addBreadcrumb({
      category: "twilio-video",
      message: eventName,
      data,
      level: "info",
    });
    return;
  }

  // Fallback for any event not in a routing table — treated as a breadcrumb
  Sentry.addBreadcrumb({
    category: "twilio-video",
    message: eventName,
    data,
    level: "info",
  });
};

/**
 * Log a Twilio error. Captures the Error as a Sentry exception with the event
 * name and any additional data attached as extras. Use this when an actual Error
 * object is available (connect failure, track publication failure, SDK error).
 */
export const logTwilioError = (
  eventName: string,
  error: Error,
  data?: TwilioEventData,
): void => {
  devError(`[twilio-event] ${eventName}`, error, data ?? "");

  Sentry.withScope((scope) => {
    scope.setExtra("twilio_event", eventName);
    if (data) scope.setExtras(data);
    Sentry.captureException(error);
  });
};

/**
 * Call on component teardown. Clears the Twilio-specific scope tags so they
 * don't bleed into unrelated Sentry events after the session ends.
 */
export const endTwilioSession = (): void => {
  Sentry.configureScope((scope) => {
    scope.setTag("feature", (undefined as unknown) as string);
    scope.setTag("platform", (undefined as unknown) as string);
    scope.setTag("session_role", (undefined as unknown) as string);
    scope.setTag("room_sid", (undefined as unknown) as string);
    scope.setContext("twilio_session", null);
  });
};
